<template>
  <div class="admin-statistics">
    <!-- Header -->
    <div class="admin-header">
      <h1 class="admin-title">
        <i class="fas fa-chart-bar"></i>
        İstatistikler Yönetimi
      </h1>
      <div class="header-actions">
        <button @click="calculateAutoStatistics" class="btn btn-calculate" :disabled="calculating">
          <i :class="calculating ? 'fas fa-spinner fa-spin' : 'fas fa-calculator'"></i>
          {{ calculating ? 'Hesaplanıyor...' : 'Otomatik Hesapla' }}
        </button>
        <button @click="addNewStatistic" class="btn btn-add">
          <i class="fas fa-plus"></i>
          Yeni İstatistik Ekle
        </button>
      </div>
    </div>

    <!-- Statistics Summary -->
    <div class="stats-summary">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-list"></i>
        </div>
        <div class="stat-info">
          <div class="stat-number">{{ statistics.length }}</div>
          <div class="stat-label">Toplam İstatistik</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon active">
          <i class="fas fa-eye"></i>
        </div>
        <div class="stat-info">
          <div class="stat-number">{{ activeCount }}</div>
          <div class="stat-label">Aktif</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon inactive">
          <i class="fas fa-eye-slash"></i>
        </div>
        <div class="stat-info">
          <div class="stat-number">{{ inactiveCount }}</div>
          <div class="stat-label">Pasif</div>
        </div>
      </div>
    </div>

    <!-- Filter Actions -->
    <div class="filter-actions">
      <button 
        @click="filterStatistics(null)" 
        :class="{ active: currentFilter === null }"
        class="btn btn-filter"
      >
        <i class="fas fa-list"></i>
        Tümü ({{ statistics.length }})
      </button>
      <button 
        @click="filterStatistics('active')" 
        :class="{ active: currentFilter === 'active' }"
        class="btn btn-filter"
      >
        <i class="fas fa-eye"></i>
        Aktif ({{ activeCount }})
      </button>
      <button 
        @click="filterStatistics('inactive')" 
        :class="{ active: currentFilter === 'inactive' }"
        class="btn btn-filter"
      >
        <i class="fas fa-eye-slash"></i>
        Pasif ({{ inactiveCount }})
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>İstatistikler yükleniyor...</p>
    </div>

    <!-- Statistics List -->
    <div v-if="!loading" class="statistics-container">
      
      <!-- No Statistics Message -->
      <div v-if="filteredStatistics.length === 0" class="no-statistics">
        <i class="fas fa-chart-bar"></i>
        <p v-if="currentFilter === null">
          Henüz istatistik eklenmemiş. İlk istatistiğinizi eklemek için "Yeni İstatistik Ekle" butonuna tıklayın.
        </p>
        <p v-else>
          {{ currentFilter === 'active' ? 'Aktif' : 'Pasif' }} istatistik bulunamadı.
        </p>
      </div>

      <!-- Statistics List -->
      <div v-for="(statistic, index) in filteredStatistics" :key="statistic.id || index" class="statistic-item">
        <div class="statistic-header">
          <div class="statistic-info">
            <h3>{{ statistic.label_tr || 'Yeni İstatistik' }}</h3>
            <div class="statistic-meta">
              <span class="statistic-value">{{ formatNumber(statistic.value) }}</span>
              <span class="statistic-status" :class="{ active: statistic.is_active, inactive: !statistic.is_active }">
                {{ statistic.is_active ? 'Aktif' : 'Pasif' }}
              </span>
              <span class="statistic-order">Sıra: {{ statistic.sort_order }}</span>
            </div>
          </div>
          <div class="statistic-actions">
            <button @click="toggleStatistic(index)" class="btn btn-toggle">
              <i :class="statistic.expanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>
            <button @click="deleteStatistic(statistic, index)" class="btn btn-delete" :disabled="submitting">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <!-- Statistic Form (Expanded) -->
        <div v-if="statistic.expanded" class="statistic-form">
          <div class="form-section">
            <!-- Durum ve Sıra -->
            <div class="form-row">
              <div class="form-group">
                <label>Durum</label>
                <div class="status-toggle">
                  <label class="toggle-label">
                    <input 
                      v-model="statistic.is_active" 
                      type="checkbox" 
                      :true-value="1" 
                      :false-value="0"
                    />
                    <span class="toggle-text">{{ statistic.is_active ? 'Aktif' : 'Pasif' }}</span>
                  </label>
                </div>
              </div>
              <div class="form-group">
                <label>Sıra No</label>
                <input 
                  v-model.number="statistic.sort_order" 
                  type="number" 
                  min="1"
                  placeholder="1"
                />
              </div>
            </div>

            <!-- Etiketler -->
            <div class="form-row">
              <div class="form-group">
                <label>Etiket (Türkçe) *</label>
                <input 
                  v-model="statistic.label_tr" 
                  type="text" 
                  required 
                  placeholder="Örn: Tamamlanan Proje"
                />
              </div>
              <div class="form-group">
                <label>Etiket (İngilizce)</label>
                <input 
                  v-model="statistic.label_en" 
                  type="text" 
                  placeholder="Ex: Completed Projects"
                />
              </div>
            </div>

            <!-- Değer ve İkon -->
            <div class="form-row">
              <div class="form-group">
                <label>Değer *</label>
                <input 
                  v-model.number="statistic.value" 
                  type="number" 
                  required 
                  min="0"
                  placeholder="100"
                />
              </div>
              <div class="form-group">
                <label>İkon (Font Awesome)</label>
                <input 
                  v-model="statistic.icon" 
                  type="text" 
                  placeholder="fas fa-chart-line"
                />
                <small class="form-help">
                  Font Awesome sınıfı (örn: fas fa-chart-line)
                  <a href="https://fontawesome.com/icons" target="_blank">İkon Listesi</a>
                </small>
              </div>
            </div>

            <!-- İkon Önizleme -->
            <div v-if="statistic.icon" class="icon-preview">
              <div class="preview-item">
                <i :class="statistic.icon"></i>
                <span>{{ formatNumber(statistic.value) }}</span>
                <small>{{ statistic.label_tr }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Actions -->
    <div v-if="!loading && statistics.length > 0" class="form-actions">
      <button @click="saveAllStatistics" :disabled="submitting" class="btn btn-submit">
        <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
        {{ submitting ? 'Kaydediliyor...' : 'Tümünü Kaydet' }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { adminApi } from '@/services/adminApi'

export default {
  name: 'AdminStatistics',
  setup() {
    // Reactive Data
    const loading = ref(false)
    const submitting = ref(false)
    const calculating = ref(false)
    const statistics = ref([])
    const currentFilter = ref(null)

    // Computed Properties
    const activeCount = computed(() => 
      statistics.value.filter(stat => stat.is_active === 1 || stat.is_active === true).length
    )

    const inactiveCount = computed(() => 
      statistics.value.filter(stat => stat.is_active === 0 || stat.is_active === false).length
    )

    const filteredStatistics = computed(() => {
      if (currentFilter.value === null) {
        return statistics.value
      } else if (currentFilter.value === 'active') {
        return statistics.value.filter(stat => stat.is_active === 1 || stat.is_active === true)
      } else if (currentFilter.value === 'inactive') {
        return statistics.value.filter(stat => stat.is_active === 0 || stat.is_active === false)
      }
      return statistics.value
    })

    // Methods
    const loadStatistics = async () => {
      try {
        loading.value = true
        const response = await adminApi.getStatistics()
        console.log('Statistics API response:', response)
        
        if (response && response.success && response.data) {
          // Her istatistiğe expanded property ekle
          statistics.value = response.data.map(stat => ({
            ...stat,
            expanded: false,
            is_active: parseInt(stat.is_active),
            value: parseInt(stat.value),
            sort_order: parseInt(stat.sort_order || 999)
          }))
        }
      } catch (error) {
        console.error('İstatistikler yüklenemedi:', error)
        alert('İstatistikler yüklenemedi. Lütfen tekrar deneyin.')
      } finally {
        loading.value = false
      }
    }

    const addNewStatistic = () => {
      const newStatistic = {
        id: null,
        label_tr: '',
        label_en: '',
        value: 0,
        icon: '',
        sort_order: statistics.value.length + 1,
        is_active: 1,
        expanded: true
      }
      statistics.value.push(newStatistic)
    }

    const toggleStatistic = (index) => {
      statistics.value[index].expanded = !statistics.value[index].expanded
    }

    const deleteStatistic = async (statistic, index) => {
      if (!confirm('Bu istatistiği silmek istediğinizden emin misiniz?')) {
        return
      }

      try {
        if (statistic.id) {
          // Veritabanından sil
          const response = await adminApi.deleteStatistic(statistic.id)
          if (!response || !response.success) {
            alert('İstatistik silinemedi. Lütfen tekrar deneyin.')
            return
          }
        }
        
        // Listeden kaldır
        statistics.value.splice(index, 1)
        alert('İstatistik başarıyla silindi!')
      } catch (error) {
        console.error('İstatistik silinemedi:', error)
        alert('İstatistik silinemedi. Lütfen tekrar deneyin.')
      }
    }

    const saveAllStatistics = async () => {
      try {
        submitting.value = true

        // Boş etiketli istatistikleri kontrol et
        const emptyStatistics = statistics.value.filter(stat => !stat.label_tr.trim())
        if (emptyStatistics.length > 0) {
          alert('Lütfen tüm istatistikler için Türkçe etiket girin.')
          return
        }

        // Geçersiz değerleri kontrol et
        const invalidStatistics = statistics.value.filter(stat => 
          stat.value === null || stat.value === undefined || isNaN(stat.value)
        )
        if (invalidStatistics.length > 0) {
          alert('Lütfen tüm istatistikler için geçerli değer girin.')
          return
        }

        const statisticsData = {
          statistics: statistics.value.map(stat => ({
            id: stat.id,
            label_tr: stat.label_tr,
            label_en: stat.label_en,
            value: stat.value,
            icon: stat.icon,
            sort_order: stat.sort_order,
            is_active: stat.is_active
          }))
        }

        const response = await adminApi.updateStatistics(statisticsData)
        
        if (response && response.success) {
          alert('Tüm istatistikler başarıyla güncellendi!')
          // Güncellenmiş veriyi yeniden yükle
          await loadStatistics()
        } else {
          alert('Güncelleme sırasında bir hata oluştu.')
        }
      } catch (error) {
        console.error('İstatistikler güncellenemedi:', error)
        alert('İstatistikler güncellenemedi. Lütfen tekrar deneyin.')
      } finally {
        submitting.value = false
      }
    }

    const filterStatistics = (filter) => {
      currentFilter.value = filter
    }

    const calculateAutoStatistics = async () => {
      try {
        calculating.value = true
        const response = await adminApi.calculateStatistics()
        console.log('Auto calculation response:', response)
        
        if (response && response.success) {
          alert('İstatistikler otomatik hesaplandı ve güncellendi!')
          // Güncellenmiş veriyi yükle
          await loadStatistics()
        } else {
          alert('Otomatik hesaplama sırasında bir hata oluştu.')
        }
      } catch (error) {
        console.error('Otomatik hesaplama başarısız:', error)
        alert('Otomatik hesaplama başarısız. Lütfen tekrar deneyin.')
      } finally {
        calculating.value = false
      }
    }

    const formatNumber = (num) => {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toString()
    }

    // Lifecycle
    onMounted(() => {
      loadStatistics()
    })

    return {
      // Data
      loading,
      submitting,
      calculating,
      statistics,
      currentFilter,
      
      // Computed
      activeCount,
      inactiveCount,
      filteredStatistics,
      
      // Methods
      loadStatistics,
      addNewStatistic,
      toggleStatistic,
      deleteStatistic,
      saveAllStatistics,
      calculateAutoStatistics,
      filterStatistics,
      formatNumber
    }
  }
}
</script>

<style scoped>
/* Container */
.admin-statistics {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Header */
.admin-header {
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.admin-title {
  font-size: 32px;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 15px;
}

.admin-title i {
  color: #3498db;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Statistics Summary */
.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 15px;
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ecf0f1;
  color: #7f8c8d;
}

.stat-icon.active {
  background: #d4edda;
  color: #27ae60;
}

.stat-icon.inactive {
  background: #f8d7da;
  color: #e74c3c;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
}

.stat-label {
  font-size: 14px;
  color: #7f8c8d;
}

/* Filter Actions */
.filter-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.btn-filter {
  background: #ecf0f1;
  color: #7f8c8d;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-filter:hover {
  background: #bdc3c7;
  color: #2c3e50;
}

.btn-filter.active {
  background: #3498db;
  color: white;
}

/* Loading */
.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* No Statistics */
.no-statistics {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.no-statistics i {
  font-size: 48px;
  color: #bdc3c7;
  margin-bottom: 20px;
}

/* Statistics Container */
.statistics-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Statistic Item */
.statistic-item {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  overflow: hidden;
}

.statistic-header {
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.statistic-info h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #2c3e50;
}

.statistic-meta {
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
}

.statistic-value {
  font-size: 20px;
  font-weight: bold;
  color: #3498db;
}

.statistic-status {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.statistic-status.active {
  background: #d4edda;
  color: #155724;
}

.statistic-status.inactive {
  background: #f8d7da;
  color: #721c24;
}

.statistic-order {
  font-size: 12px;
  color: #6c757d;
}

.statistic-actions {
  display: flex;
  gap: 10px;
}

/* Statistic Form */
.statistic-form {
  padding: 30px;
}

.form-section {
  margin-bottom: 20px;
}

.status-toggle {
  margin-top: 8px;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  color: #2c3e50;
  cursor: pointer;
  padding: 8px 0;
}

.toggle-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.toggle-text {
  font-size: 14px;
  font-weight: 500;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 8px;
  font-weight: 500;
  color: #2c3e50;
}

.form-group input,
.form-group textarea,
.form-group select {
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  font-family: inherit;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #3498db;
}

.form-help {
  margin-top: 5px;
  font-size: 12px;
  color: #7f8c8d;
}

.form-help a {
  color: #3498db;
  text-decoration: none;
  margin-left: 5px;
}

.form-help a:hover {
  text-decoration: underline;
}

/* Icon Preview */
.icon-preview {
  margin-top: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.preview-item {
  text-align: center;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 200px;
  margin: 0 auto;
}

.preview-item i {
  font-size: 32px;
  color: #3498db;
}

.preview-item span {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
}

.preview-item small {
  font-size: 12px;
  color: #7f8c8d;
}

/* Form Actions */
.form-actions {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}

/* Buttons */
.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-calculate {
  background: #f39c12;
  color: white;
}

.btn-calculate:hover:not(:disabled) {
  background: #e67e22;
  transform: translateY(-1px);
}

.btn-add {
  background: #27ae60;
  color: white;
}

.btn-add:hover {
  background: #219a52;
  transform: translateY(-1px);
}

.btn-toggle {
  background: #3498db;
  color: white;
  padding: 8px 12px;
}

.btn-toggle:hover {
  background: #2980b9;
}

.btn-delete {
  background: #e74c3c;
  color: white;
  padding: 8px 12px;
}

.btn-delete:hover:not(:disabled) {
  background: #c0392b;
}

.btn-submit {
  background: #3498db;
  color: white;
  padding: 15px 30px;
  font-size: 16px;
}

.btn-submit:hover:not(:disabled) {
  background: #2980b9;
  transform: translateY(-1px);
}

.btn:disabled {
  background: #95a5a6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive */
@media (max-width: 768px) {
  .admin-statistics {
    padding: 15px;
  }

  .admin-header {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }

  .admin-title {
    font-size: 24px;
  }

  .stats-summary {
    grid-template-columns: 1fr;
  }

  .statistic-form {
    padding: 20px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .statistic-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }

  .statistic-meta {
    justify-content: center;
  }

  .filter-actions {
    justify-content: center;
  }
}
</style>